import { BlogAndSubscribeDetails } from "../types"
import { getModalPath, ModalRoute } from "../utils"
import { subscribeUserToBlog } from "@/lib/api/blogs"
import { isErrorResponse } from "@types"
import { captureException } from "@sentry/nextjs"
import { subscribeError } from "../tracking"
import { isUserLoggedIn } from "@/lib/utils/user"

/**
 * Handles the completion of the enter email form.
 *
 * When the user completes the enter email form, we need to redirect them to the
 * next page. That next page is either the create account page or the login page,
 * depending on whether the user already exists in our database or not.
 *
 * This needs to be in a client component, otherwise we get the error:
 * "Event handlers cannot be passed to Client Component props."
 *
 * @param blogDetails - The blog details.
 * @returns The next redirect path.
 */
export async function onComplete(
  blogDetails: BlogAndSubscribeDetails,
  email?: string
): Promise<string> {
  const [isLoggedIn, resp] = await Promise.all([
    isUserLoggedIn(),
    // Subscribe user to the blog immediately.
    // TODO: Add referrer info.
    subscribeUserToBlog({
      lowercaseBlogUrl: blogDetails.lowercase_url,
      blogId: blogDetails.id,
      email,
    }),
  ])

  const isError = isErrorResponse(resp)

  const alreadySubscribed = isError && resp.code === "ALREADY_SUBSCRIBED"

  if (alreadySubscribed) {
    // If the user is already subscribed to the blog and logged in, redirect them to the already subscribed page.
    console.log(
      "User is already subscribed to blog, continuing to already subscribed page.",
      resp
    )

    return getModalPath(ModalRoute.ALREADY_SUBSCRIBED, blogDetails)
  } else if (isError) {
    // If there was any other error subscribing the user to the blog, log an error and return.
    console.error("Error subscribing user to blog!", resp)

    // Track the error on Amplitude.
    subscribeError(blogDetails?.id || "", resp.msg)

    // Log to Sentry.
    const err = new Error("Error subscribing user to blog!")
    captureException(err)

    throw err
  }

  const nextState = resp && "nextState" in resp && resp?.nextState

  console.log("EnterEmail onComplete nextState", nextState)

  switch (nextState) {
    case "CONTINUE":
      // Redirect to communities page.
      return getModalPath(ModalRoute.CHOOSE_COMMUNITIES, blogDetails, email)

    default:
      // This should never happen.
      const defaultErrorMessage =
        "Unexpected next state! Unknown state: " + nextState

      console.error(defaultErrorMessage)

      // Track the error on Amplitude.
      subscribeError(blogDetails?.id || "", defaultErrorMessage)

      // Log to Sentry.
      const defaultErr = new Error(defaultErrorMessage)
      captureException(defaultErr)

      throw defaultErr
  }
}
